import api from '../lib/axios';
import type { ApiResponse, ProfileColorKey } from '@maios/shared';

// --- Types ---

export interface ChatSender {
  userId: string;
  name: string;
  profileImage: string | null;
  // CHG-131 REQ-CP-018: 16색 팔레트 키 + 닉네임 (서버 sender select 확장)
  // nickname은 서버에서 visibility 체크 후 비공개면 null로 내려옴 → 클라이언트는 firstName/lastName 또는 name 사용
  displayColor?: ProfileColorKey | null;
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface ChatMention {
  issueKey: string;
  issueId: string;
}

// CHG-131 REQ-CP-017: 사용자 멘션 (mentions 배열에 혼재) — displayColor/nickname 포함
export interface ChatUserMention {
  userId: string;
  name: string;
  offset: number;
  length: number;
  displayColor?: ProfileColorKey | null;
  nickname?: string | null;
}

// REQ-V35-001: 멘션된 이슈의 resolve된 상세 정보
export interface ResolvedMention {
  issueKey: string;
  issueId: string;
  title: string;
  statusName: string;
  statusColor: string;
  issueType: string;
  // CHG-133 Step 7 follow-up
  assignee: {
    userId: string;
    name: string;
    profileImage: string | null;
    displayColor?: string | null;
    nickname?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
}

// CHG-123 REQ-V123-013: 메시지로부터 변환된 작업/할 일 요약
export interface LinkedIssueSummary {
  issueId: string;
  issueKey: string;
  title: string;
  statusName: string;
  statusColor: string;
  projectKey: string;
}
export interface LinkedTodoSummary {
  todoId: string;
  title: string;
  completed: boolean;
}
export interface LinkedResources {
  issues: LinkedIssueSummary[];
  todos: LinkedTodoSummary[];
}

export interface ChatMessage {
  messageId: string;
  content: string;
  mentions: ChatMention[];
  resolvedMentions?: ResolvedMention[];
  reactions?: Array<{ emoji: string; count: number; users: string[]; reacted: boolean }>;
  attachments?: Array<{ id: string; fileName: string; fileSize: number; mimeType: string; url: string; thumbnailUrl?: string | null }>;
  isEdited?: boolean;
  editedAt?: string | null;
  isDeleted?: boolean;
  createdAt: string;
  sender: ChatSender;
  // CHG-075-P4: 스레드
  parentMessageId?: string;
  threadCount?: number;
  lastThreadAt?: string | null;
  // CHG-131: 스레드 프리뷰 아바타도 ChatSender 구조 (firstName/lastName 포함, visibility 적용된 nickname)
  threadPreview?: ChatSender[];
  isPinned?: boolean;
  // CHG-123: 메시지로부터 변환된 작업/할 일
  linkedResources?: LinkedResources;
}

export interface ChatMessagesResponse {
  messages: ChatMessage[];
  hasMore: boolean;
  nextCursor: string | null;
}

// --- API calls ---

export async function fetchMessages(
  projectId: string,
  params?: { cursor?: string; limit?: number },
) {
  const query = new URLSearchParams();
  if (params?.cursor) query.set('cursor', params.cursor);
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  const res = await api.get<ApiResponse<ChatMessagesResponse>>(
    `/projects/${projectId}/chat/messages${qs ? `?${qs}` : ''}`,
  );
  return res.data.data!;
}

// CHG-131: 사용자 멘션 데이터 (ChatInput → 서버 저장)
export interface PostMessageMention {
  userId: string;
  name: string;
  offset: number;
  length: number;
}

export async function postMessage(projectId: string, content: string, mentions?: PostMessageMention[]) {
  const res = await api.post<ApiResponse<ChatMessage>>(
    `/projects/${projectId}/chat/messages`,
    { content, mentions: mentions && mentions.length > 0 ? mentions : undefined },
  );
  return res.data.data!;
}

export async function markAsRead(projectId: string) {
  await api.post(`/projects/${projectId}/chat/read`);
}

export async function fetchUnread(projectId: string) {
  const res = await api.get<ApiResponse<{ hasUnread: boolean }>>(
    `/projects/${projectId}/chat/unread`,
  );
  return res.data.data!;
}
